import {TiersResponse} from "@irati/types"
import Validation from "@irati/utils/validation"
import {twoDecimalPlaces} from "@irati/utils/validation/regex/number"
import {isNil} from "lodash"
import {useReducer} from "react"

export const currencySchema = Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
  .matches(twoDecimalPlaces, "wallet.reward.admin.distribute.validation.decimal")
  .test("Positive", "wallet.reward.admin.distribute.validation.pointsPerUser.required", (val) => val > 0 || isNil(val))

const durations = {
  month: 12,
  quarter: 4,
  year: 1
} as const
type Duration = keyof typeof durations

export interface State {
  rawUserAmount?: number
  amount?: number
  annualAmount?: number
  status: "invalid" | "valid" | "none"
  duration: Duration
  tiers?: TiersResponse[]
  currentTier?: string
  serviceFee?: {
    manual: number
    subscription: number
  }
  _monthMultiplier: number
}

type Action =
  | {type: "setAmount"; payload: number}
  | {type: "setDuration"; payload: State["duration"]}
  | {type: "setTiers"; payload: TiersResponse[]}

const initialState: State = {
  status: "none",
  duration: "month",
  _monthMultiplier: durations["month"]
}

const calculateServiceFee = ({amount, tiers, annualAmount}: State): Required<Pick<State, "currentTier" | "serviceFee">> | null => {
  if (!amount || !tiers || !annualAmount) {
    return null
  }

  const manualCost = amount
  const manualTier = tiers.find(({startValue, endValue}) => (endValue === -1 ? true : manualCost >= startValue && manualCost <= endValue))
  const subscriptionTier = tiers.find(({startValue, endValue}) =>
    endValue === -1 ? true : annualAmount >= startValue && annualAmount <= endValue
  )

  if (!manualTier || !subscriptionTier) {
    return null
  }

  return {
    serviceFee: {
      manual: annualAmount * manualTier.tierServiceFeeRate!,
      subscription: annualAmount * subscriptionTier.tierServiceFeeRate!
    },
    currentTier: subscriptionTier.uuid
  }
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setAmount": {
      // all calculations are in cents, and we always floor cents for service fees
      const amount = Math.floor(action.payload)
      const valid = currencySchema.isValidSync(`${amount}`)
      const annualAmount = Math.floor(amount) * state._monthMultiplier

      return {
        ...state,
        status: valid ? "valid" : "invalid",
        rawUserAmount: action.payload,
        amount,
        annualAmount,
        ...calculateServiceFee({...state, amount, annualAmount})
      }
    }
    case "setDuration": {
      const {amount} = state
      const annualAmount = amount ? amount * durations[action.payload] : undefined

      return {
        ...state,
        annualAmount,
        duration: action.payload,
        _monthMultiplier: durations[action.payload],
        ...calculateServiceFee({...state, annualAmount})
      }
    }
    case "setTiers": {
      const tiers = action.payload

      return {
        ...state,
        tiers,
        ...calculateServiceFee({...state, tiers})
      }
    }
    default:
      return state
  }
}

export const useModalReducer = () => useReducer(reducer, initialState)

export const isDuration = (val: unknown): val is Duration => typeof val === "string" && Object.keys(durations).includes(val)
